import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types for damage checklist data
export interface DamageChecklistItem {
  // API-provided fields
  ROWKEY?: number;
  LOC?: string;
  AISLE?: string;
  BAY_NUM?: string;
  LEVEL?: number;
  SLOT_ID?: number;
  Status?: boolean;
  QTYPE?: string;
  QNUM?: number;
  COMMENTS?: string;
  INSTANCES?: number;
  SEVERITY?: string;
  DOR?: string;
  DORCOMPLETION?: string | null;
  LAST_UPDT_BY?: string;
  LAST_UPDT_DTM?: string;
  DESC1?: string;
  DESC2?: string | null;
  UID?: string;
  PW?: string;
  LAST_UPDT_BY_USERNAME?: string;
  WALK_DATE?: string;
  WALK_STARTED_BY?: string;
  SHORT_FORM?: string;

  // Additional fields for the UI
  id?: number | string;
  aisle?: string;
  bay?: string;
  level?: string | number;
  slotid?: number | string;
  typeofdamage?: string;
  noofinst?: number;
  severity?: string;
  obsrecdate?: string;
  tgtdtofrep?: string;
  actdtofrep?: string | null;
  status?: string | boolean;
  comments?: string;
  rawData?: any;
}

// Interface for the state
interface RackingDamageChecklistState {
  filterOptions: Record<string, Array<{ id: string; displayText: string }>>;
  damageItems: DamageChecklistItem[];
  loading: boolean;
  error: string | null;
  filters: {
    aisle: string;
    bay: string;
    level: string;
    period: string;
    week: string;
    typeOfDamage: string;
    status: string;
    createdBy: string;
    severity: string;
  };
  showActiveItemsOnly: boolean;
  lastEmptyFetchKey?: string;
}

// Initial state
const initialState: RackingDamageChecklistState = {
  damageItems: [],
  loading: false,
  error: null,
  filters: {
    aisle: "",
    bay: "",
    level: "",
    period: "",
    week: "",
    typeOfDamage: "",
    status: "",
    createdBy: "",
    severity: "",
  },
  showActiveItemsOnly: true,
  filterOptions: {},
  lastEmptyFetchKey: undefined,
};

// Create the slice
const rackingDamageChecklistSlice = createSlice({
  name: "rackingDamageChecklist",
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      if (action.payload) {
        state.loading = false;
      }
    },

    // Set damage checklist data
    setDamageItems: (state, action: PayloadAction<DamageChecklistItem[]>) => {
      console.log("setDamageItems action payload:", action.payload);
      console.log("Is payload array?", Array.isArray(action.payload));

      // Ensure we have an array
      const data = Array.isArray(action.payload) ? action.payload : [];

      state.damageItems = data;

      // No filtering here; all items are set in damageItems only
      console.log(
        "State after update - damageItems length:",
        state.damageItems.length
      );
    },

    // Set filters
    setFilters: (
      state,
      action: PayloadAction<{
        aisle?: string;
        bay?: string;
        level?: string;
        period?: string;
        week?: string;
        typeOfDamage?: string;
        status?: string;
        createdBy?: string;
        severity?: string;
      }>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
      // No filtering; keep all items in damageItems only
    },

    // Set filter options (from API or elsewhere)
    setFilterOptions: (
      state,
      action: PayloadAction<
        Record<string, Array<{ id: string; displayText: string }>>
      >
    ) => {
      state.filterOptions = action.payload;
    },

    // Toggle showing active items only
    toggleActiveItemsOnly: (state) => {
      state.showActiveItemsOnly = !state.showActiveItemsOnly;

      // No filtering; keep all items in damageItems only
    },

    // Reset filters
    resetFilters: (state) => {
      state.filters = initialState.filters;

      // No filtering; keep all items in damageItems only
    },

    // Set lastEmptyFetchKey
    setLastEmptyFetchKey: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.lastEmptyFetchKey = action.payload;
    },
  },
});

export const {
  setLoading,
  setError,
  setDamageItems,
  setFilters,
  setFilterOptions,
  toggleActiveItemsOnly,
  resetFilters,
  setLastEmptyFetchKey,
} = rackingDamageChecklistSlice.actions;

export default rackingDamageChecklistSlice.reducer;
